import { useState } from "react";
import { Button, Input, Modal, Select, Tooltip } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import medicalReviewApi, {
  MedicalReviewApiError,
} from "@/services/medicalReviewApi";
import { useAppNotification } from "@/hooks/useAppNotification";
import { invalidateReviewDatasets } from "./medicalReview";

type Mode = "assign" | "escalate";

/**
 * Who looks at these records next — as two buttons that sit beside "Mark
 * reviewed", because they are alternatives to reviewing it yourself.
 *
 * There is no Claim: marking a record reviewed already *is* taking it on, so a
 * separate button for the same intent was two ways to say one thing. Assigning
 * directs someone else's queue and needs the administrator role; escalating
 * hands the record upward with a question and needs only a note.
 */
export default function ReviewAssignControl({
  trialId,
  itemIds,
  canAdmin,
  onDone,
}: {
  trialId: string;
  itemIds: number[];
  canAdmin: boolean;
  onDone?: () => void;
}) {
  const [mode, setMode] = useState<Mode | null>(null);
  const [assigneeId, setAssigneeId] = useState<string | undefined>();
  const [note, setNote] = useState("");
  const queryClient = useQueryClient();
  const { openSuccessNotification, openErrorNotification, contextHolder } =
    useAppNotification();

  const { data: assignees, isLoading: assigneesLoading } = useQuery({
    queryKey: ["medical-review-assignees", trialId],
    queryFn: () => medicalReviewApi.assignees(trialId),
    enabled: mode !== null,
    staleTime: 5 * 60 * 1000,
  });

  const close = () => {
    setMode(null);
    setAssigneeId(undefined);
    setNote("");
  };

  const mutation = useMutation({
    mutationFn: (body: Parameters<typeof medicalReviewApi.assign>[1]) =>
      medicalReviewApi.assign(trialId, body),
    onSuccess: (result) => {
      invalidateReviewDatasets(queryClient);
      // Ownership shows on the drawer, the subject rollup and the counts too.
      queryClient.invalidateQueries({ queryKey: ["medical-review-item"] });
      queryClient.invalidateQueries({ queryKey: ["medical-review-subject"] });
      queryClient.invalidateQueries({
        queryKey: ["medical-review-counts", trialId],
      });
      close();
      onDone?.();
      const count = result.assigned.length;
      const who = result.assignee?.name;
      openSuccessNotification({
        title:
          result.assignment_kind === "claimed"
            ? `${count} record${count === 1 ? "" : "s"} claimed`
            : result.assignment_kind === "escalated"
              ? `Escalated to ${who}`
              : result.assignment_kind === "assigned"
                ? `Assigned to ${who}`
                : "Returned to the queue",
        description:
          result.assignment_kind === "escalated"
            ? "They have been notified, with your note."
            : result.assignment_kind === "assigned"
              ? "They have been notified."
              : undefined,
      });
    },
    onError: (error: MedicalReviewApiError) =>
      openErrorNotification({
        title: "Could not change who owns this",
        description: error.message,
      }),
  });

  const people = assignees?.people ?? [];
  // Escalation goes to the people whose opinion ends the question.
  const options = (
    mode === "escalate" ? people.filter((p) => p.senior) : people
  ).map((person) => ({
    value: person.id,
    label: person.senior ? `${person.name} · Medical Monitor` : person.name,
  }));

  const disabled = itemIds.length === 0;
  const escalateNeedsNote = mode === "escalate" && !note.trim();

  return (
    <>
      {contextHolder}
      {canAdmin && (
        <Button disabled={disabled} onClick={() => setMode("assign")}>
          Assign Review
        </Button>
      )}
      <Tooltip title="Ask a medical monitor to decide, with a note saying what you need from them.">
        <Button disabled={disabled} onClick={() => setMode("escalate")}>
          Escalate Review
        </Button>
      </Tooltip>

      <Modal
        title={
          mode === "escalate" ? "Escalate for a decision" : "Assign review"
        }
        open={mode !== null}
        onCancel={close}
        okText={mode === "escalate" ? "Escalate" : "Assign"}
        okButtonProps={{
          disabled: !assigneeId || escalateNeedsNote,
          loading: mutation.isPending,
        }}
        onOk={() =>
          mutation.mutate({
            items: itemIds,
            assignee_id: assigneeId,
            escalate: mode === "escalate",
            comment: note,
          })
        }
        destroyOnHidden
      >
        <div className="flex flex-col gap-3 py-2">
          <Select
            showSearch
            optionFilterProp="label"
            loading={assigneesLoading}
            value={assigneeId}
            onChange={setAssigneeId}
            options={options}
            placeholder={
              mode === "escalate"
                ? "Which medical monitor?"
                : "Who should review this?"
            }
            className="w-full"
            notFoundContent={
              assigneesLoading ? null : "Nobody else on this trial to pick."
            }
          />
          <Input.TextArea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            autoSize={{ minRows: 3, maxRows: 6 }}
            placeholder={
              mode === "escalate"
                ? "What do you want them to decide? (required)"
                : "Anything they should know (optional)"
            }
          />
          <p className="m-0 text-xs text-[#8FA5A0]">
            {itemIds.length} record{itemIds.length === 1 ? "" : "s"}
          </p>
        </div>
      </Modal>
    </>
  );
}
